import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Download, Search, CheckCircle2, XCircle, Clock, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGE = {
  'Concluído': { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  'Anulado': { cls: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  'Pendente': { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  'Pedido': { cls: 'bg-sky-100 text-sky-800 border-sky-200', icon: FileText },
  'Em curso': { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
};

export const PartnerConversionsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/partners/conversions`)
      .then(res => { if (!cancelled) setItems(res.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = items.filter(it => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (it.nome_cliente || '').toLowerCase().includes(q)
      || (it.empresa || '').toLowerCase().includes(q)
      || (it.servico || '').toLowerCase().includes(q)
      || (it.angariador || '').toLowerCase().includes(q);
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/partners/conversions/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'conversoes.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const counts = {
    total: items.length,
    concluido: items.filter(i => i.estado === 'Concluído').length,
    pendente: items.filter(i => i.estado === 'Pendente').length,
    anulado: items.filter(i => i.estado === 'Anulado').length,
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="conversions-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Conversões</h1>
            <p className="text-sm text-slate-500 mt-1">Lista das suas leads que foram convertidas em contratos.</p>
          </div>
          <Button onClick={handleExport} disabled={exporting || items.length === 0} className="bg-[#2D3E6F] hover:bg-[#1a2a4a] rounded-full gap-2" data-testid="export-conversions">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Exportar Excel
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-4 rounded-xl border border-slate-100 bg-white">
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-800">{counts.total}</p>
          </div>
          <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/40">
            <p className="text-xs text-emerald-700">Concluídos</p>
            <p className="text-2xl font-bold text-emerald-800">{counts.concluido}</p>
          </div>
          <div className="p-4 rounded-xl border border-amber-100 bg-amber-50/40">
            <p className="text-xs text-amber-700">Pendentes</p>
            <p className="text-2xl font-bold text-amber-800">{counts.pendente}</p>
          </div>
          <div className="p-4 rounded-xl border border-red-100 bg-red-50/40">
            <p className="text-xs text-red-700">Anulados</p>
            <p className="text-2xl font-bold text-red-800">{counts.anulado}</p>
          </div>
        </div>

        <Card className="rounded-2xl border-slate-100">
          <CardHeader className="border-b border-slate-100 flex-row items-center gap-3">
            <CardTitle className="text-base font-semibold flex-1">Lista de Conversões</CardTitle>
            <div className="relative w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Procurar cliente, empresa, serviço..."
                className="pl-9 h-10 rounded-lg"
                data-testid="search-conversions"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Sem conversões para mostrar.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3 font-semibold">Código</th>
                      <th className="px-4 py-3 font-semibold">Nome Cliente</th>
                      <th className="px-4 py-3 font-semibold">Serviço</th>
                      <th className="px-4 py-3 font-semibold">Empresa</th>
                      <th className="px-4 py-3 font-semibold">Estado</th>
                      <th className="px-4 py-3 font-semibold">Data</th>
                      <th className="px-4 py-3 font-semibold">Angariador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((it, idx) => {
                      const sb = STATUS_BADGE[it.estado] || STATUS_BADGE['Em curso'];
                      const Icon = sb.icon;
                      return (
                        <tr key={it.id || idx} className="border-t border-slate-100 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 font-mono text-slate-500">#{it.codigo || '—'}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{it.nome_cliente}</td>
                          <td className="px-4 py-3 text-slate-600">{it.servico}</td>
                          <td className="px-4 py-3 text-slate-600">{it.empresa}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${sb.cls}`}>
                              <Icon className="w-3 h-3" />
                              {it.estado}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-500">{it.data_contrato || '—'}</td>
                          <td className="px-4 py-3 text-slate-600">{it.angariador}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PartnerConversionsPage;
